import { Box, Typography, Stack } from '@mui/material';
import * as dateFns from 'date-fns';
import { datesForRange } from 'src/utils/rollup';
import { currencyFormatter } from 'src/utils/currency-formatter';
import AmortizationSchedule from 'src/components/plaid/datasource/AmortizationSchedule';
import brandColors from 'src/utils/brand-colors';
import normalizeDate from 'src/utils/normalizeDate';
import {
  renderCell,
  findBudgetForSegment,
  determinePatchValues,
  forecastValue,
  nameForSegment,
  getForecastingIndex,
  getAccounts,
  valueForLiability,
  forecastSegments,
} from './utils';
import useBudget from '../budget';

export default function liabilitiesForecast({
  period,
  dateRange,
  debts,
  accounts,
  patches,
  profile,
}) {
  const loans = getAccounts(accounts, ['loan']);
  const creditCards = getAccounts(accounts, ['credit_card', 'credit card']);
  const segments = datesForRange(period, dateRange?.startDate, dateRange?.endDate, profile);
  const currentSegment = getForecastingIndex(segments);

  const budgets = useBudget({}, period, patches, segments);

  // aggregate liabilities from accounts with manually added ones
  const mergeAccountWithLiability = (acc) => {
    const liability = debts.find((debt) => debt?.account_id === acc.id);
    return { account: acc, ...liability };
  };

  const allLiabilities = [...loans, ...creditCards]
    .map(mergeAccountWithLiability)
    .concat(debts.filter((e) => e.provider === 'manual')); // add the manually added liabilities

  // create an array of amortization schedules
  const amortizations = allLiabilities
    .map((liability) => {
      try {
        // create an amortization schedule
        const amortization = amortizationForLiability(liability, budgets);
        const startDate = normalizeDate(amortization.originationDate);

        // create the forecasted data set
        const seriesData = forecastSegments(segments, (label, segment) => {
          const diff = dateFns.differenceInMonths(segment.value, startDate);
          const correspondingValue = amortization.schedule?.find((val) => val.month === diff);
          return Math.max(correspondingValue?.balance ?? 0);
        });

        return {
          id: liability.id,
          data: seriesData,
          current_balance: valueForLiability(liability, Object.values(seriesData)[currentSegment]),
          name: liability.name ?? liability.loan_name ?? liability.account?.name,
          provider: liability.provider,
          liability,
        };
      } catch (error) {
        console.warn(
          `Unable to forecast liability: ${liability.name ?? liability.account.name} (${
            liability.id ?? liability.account.id
          }): ${error.message}`
        );
        return null;
      }
    })
    .filter(Boolean);

  const rows = [
    ...amortizations.map(({ id, name, data, current_balance, provider, liability }) => ({
      id,
      primaryCategory: 'Loans',
      detailedCategory: name,
      name,
      amount: current_balance,
      data,
      provider,
      liability,
    })),
    // ...credit.map((account) => ({
    //   id: account.id,
    //   primaryCategory: 'Credit',
    //   detailedCategory: account.name,
    //   name: account.name,
    //   amount: account.current_balance,
    //   data: getSeries(account.id),
    // })),
  ].map((e, index) => ({ ...e, color: brandColors[index % brandColors.length] }));

  const nameForAccount = (id) => rows?.find((e) => e?.id === id)?.primary_category;

  const historicalSeries = rows.map((account) => {
    const res = { ...account };
    res.data = segments.map(({ label, future }) => (future ? 0 : account?.data?.[label] || 0));
    return res;
  });

  const forecastedSeries = rows.map((account) => {
    const res = { ...account };
    res.data = segments.map(({ future, label }) => (!future ? 0 : account?.data?.[label] || 0));
    return res;
  });

  const currentSegmentIndex = segments.findIndex((s) => s.current);
  const forecast = rows?.map((account) => Object.values(account?.data ?? {}));

  const currentValue = rows?.reduce(
    (acc, account) => acc + (Object.values(account?.data)?.[currentSegmentIndex] || 0),
    0
  );

  const columnReducer = (acc, data) => (data ?? [])?.map((value, i) => (acc[i] || 0) + value);

  const portfolioGrowth = forecast?.reduce(columnReducer, []);

  const baseColumns = [
    {
      field: 'primaryCategory',
      headerName: 'Primary Category',
      width: 200,
      groupingValueGetter: ({ value }) => value || '',
    },
    {
      field: 'detailedCategory',
      headerName: 'Detailed Category',
      width: 200,
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" padding={0.5}>
          <Box
            sx={{
              backgroundColor: p.row.color,
              width: '25px',
              minWidth: '25px',
              height: '25px',
              borderRadius: '100%',
              mr: 1,
            }}
          />
          <Typography>{p.row.detailedCategory}</Typography>
        </Stack>
      ),
    },
  ];

  const categoryColDef = {
    field: 'primaryCategory',
    headerName: 'Category',
    width: 150,
    valueGetter: (params) => params?.rowNode?.type === 'group',
  };

  const segmentColumns = [
    ...segments.map((segment, index) => ({
      field: segment.label,
      headerName: nameForSegment(segment, budgets, period),
      value: segment.value,
      width: 150,
      type: 'number',
      future: segment.future,
      headerClassName: index % 2 === 0 ? 'even' : 'odd',
      valueGetter: (_, row) => row?.data?.[segment.label] ?? 0,
      valueFormatter: (value) => currencyFormatter.format(value),
      renderCell: (params) =>
        renderCell({
          period,
          budgets,
          segment,
          params,
          nameForCategory: nameForAccount,
        }),
    })),
  ];

  const columns = [...baseColumns, ...segmentColumns, categoryColDef];

  return {
    allLiabilities,
    rows,
    columns,
    segments,
    forecast: portfolioGrowth,
    historicalSeries,
    forecastedSeries,
    budgets,
    currentValue,
  };
}

// normalizes various types of liabilities into a single, standardized representation for forecasting
function normalizeLiability(liability) {
  const {
    maturity_date,
    origination_date,
    expected_payoff_date,
    interest_rate_percentage,
    origination_principal_amount,
    interest_rate,
    term,
  } = liability;

  const principal = origination_principal_amount; // Loan amount
  const annualInterestRate = interest_rate_percentage ?? interest_rate?.percentage; // Annual interest rate in percent

  // Parse the origination date
  const originationDate = normalizeDate(origination_date);
  if (!dateFns.isValid(originationDate)) {
    throw new Error('Invalid origination date');
  }
  // Parse the expected payoff date if available
  const expectedPayoffDate = expected_payoff_date ? dateFns.parseISO(expected_payoff_date) : null;
  if (expectedPayoffDate && !dateFns.isValid(expectedPayoffDate)) {
    throw new Error('Invalid expected payoff date');
  }

  // Parse the maturity date if available
  const maturityDate = maturity_date ? dateFns.parseISO(maturity_date) : null;
  if (maturityDate && !dateFns.isValid(maturityDate)) {
    throw new Error('Invalid maturity date');
  }

  // Calculate the fallback end date using the term and origination date
  let fallbackEndDate = null;
  if (!expectedPayoffDate && !maturityDate && originationDate && term) {
    const termInYears = Number(term);
    if (Number.isNaN(termInYears)) {
      throw new Error('Invalid term value');
    }

    fallbackEndDate = dateFns.addYears(originationDate, termInYears);
    if (!dateFns.isValid(fallbackEndDate)) {
      throw new Error('Invalid fallback end date calculation');
    }

    // return {};
  }

  // Determine the final end date, preferring in this order:
  // 1. expected payoff date
  // 2. maturity date
  // 3. calculated end date based on term
  const end = expectedPayoffDate || maturityDate || fallbackEndDate;
  if (!dateFns.isValid(end)) {
    throw new Error('Final end date is invalid');
  }

  const loanTermInMonths = dateFns.differenceInCalendarMonths(end, originationDate);

  return {
    start: originationDate,
    end,
    principal,
    interestRate: annualInterestRate,
    months: loanTermInMonths,
  };
}

function amortizationForLiability(debt, budgets = []) {
  const { start, end, principal, interestRate, months } = normalizeLiability(debt);
  const payments = computeExtraPayments(debt, { start, end }, budgets);
  const amortization = new AmortizationSchedule(start, principal, interestRate, months, payments);

  // You can now access the amortization schedule and use it as needed
  return amortization;
}

// Apply any budgeted payments to the amortization schedule
function computeExtraPayments(liability, { start, end }, budgets) {
  // const { account } = liability;
  const segments = datesForRange('month', start, end);

  const orderedBudgets = [...budgets].reverse();
  const appliedPatches = new Map();
  const inflationRateObj = { offsetIndex: 0, rate: 1, stepSize: 1 }; // Initialize as an object with a property

  const extraPayments = segments.map((s, i) => {
    const budget = findBudgetForSegment(s.value, orderedBudgets, budgets);
    const patchValues = determinePatchValues(s.value, budget, liability.id, 'month');

    // The key used for displaying the liability in charts / spreadsheets
    const key =
      // liability?.subtype === 'mortgage' // || liability.name?.toLowerCase().includes('mortgage')
      //   ? 'LOAN_PAYMENTS_MORTGAGE_PAYMENT'
      //   : liability?.subtype === 'student'
      //   ? 'LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT'
      //   :
      liability?.id;

    // and object holding the segment payment data
    const data = { patchValues, key, inflationRateObj, inflation: 1 };
    const options = { fallbackToZero: true }; // fallback to the default
    const forecastedValue = forecastValue({ ...data, appliedPatches, options });

    return s.future ? forecastedValue : 0;
  });

  return extraPayments;
}

/*
  // Calculate the sum of APR rates based on their balances if there's no expiration date
  function calculateAPRSum(account) {
    // Check if the liability has a valid expiration date
    const expiration = account?.maturity_date ?? account?.expected_payoff_date;
    if (!expiration && account?.aprs) {
      const totalAPR =
        account.aprs.reduce((sum, apr) => sum + apr.apr_percentage, 0) / account.aprs.length;
      return 1 + totalAPR / 12 / divisorForPeriod(period);
    }
    return 0.0; // Default rate if expiration date is available
  }

  const rateForAccount = (accountId) => {
    const account = accounts.find((acc) => acc.id === accountId);
    const liability = Object.values(data)
      .flatMap(Object.values)
      .find((_liability) => _liability.id === accountId);

    if (!account || !liability) {
      return 1.0; // Default rate if account or liability not found
    }

    // Check if the liability has a valid expiration date
    const expiration = liability?.maturity_date ?? liability?.expected_payoff_date;
    if (!expiration) {
      const apr = calculateAPRSum(liability); // Default rate if already expired

      return apr;
    }

    // Calculate the remaining days until the expiration date
    const currentDate = startOfRollupPeriod(new Date(), period);
    const endDate = endOfRollupPeriod(dateFns.parseISO(expiration), period);

    // a number representing the series to compute (based on mo, qt, yr)
    const remainingSegments = countSegmentsInDateRange(currentDate, endDate, period);

    if (remainingSegments <= 0) {
      return 0.0; // Default rate if already expired
    }

    // Calculate the rate
    const rate = 1 / remainingSegments;

    return 1 - rate;
  };

function calculateOriginalAmount(account, liability) {
  switch (account.subtype) {
    case 'mortgage':
      return calculateLoanOriginalAmount(liability);
    case 'credit':
      return calculateCreditOriginalAmount(liability);
    case 'student':
      return calculateStudentLoanOriginalAmount(liability);
    default:
      return 0.0; // Default value for unknown account type
  }
}

function calculateRemainingAmount(account, liability) {
  switch (account.subtype) {
    case 'mortgage':
      return calculateMortgageOutstandingAmount(liability);
    case 'credit':
      return calculateCreditTotalOutstandingAmount(liability.aprs);
    case 'student':
      return calculateStudentLoanOutstandingAmount(liability);
    default:
      return 0; // Default value for unknown liability type
  }
}

// Function to calculate total outstanding amount for each APR type
function calculateCreditTotalOutstandingAmount(aprs) {
  // Initialize the total amount for this credit
  let totalAmount = 0;

  // Loop through each APR entry
  aprs.forEach((apr) => {
    // Add the balance_subject_to_apr to the total amount
    totalAmount += apr.balance_subject_to_apr;
  });

  return totalAmount;
}

// Function to calculate total outstanding amount for mortgages
function calculateMortgageOutstandingAmount(mortgage) {
  // Calculate the total outstanding amount
  const totalOutstandingAmount =
    mortgage.origination_principal_amount + mortgage.past_due_amount + mortgage.escrow_balance;

  return totalOutstandingAmount;
}

// Function to calculate total outstanding amount for student loans
function calculateStudentLoanOutstandingAmount(studentLoan) {
  // Calculate the total outstanding amount
  const totalOutstandingAmount =
    studentLoan.origination_principal_amount +
    studentLoan.outstanding_interest_amount +
    studentLoan.last_statement_balance;

  return totalOutstandingAmount;
}

function calculateLoanOriginalAmount(liability) {
  return liability.origination_principal_amount;
}

function calculateCreditOriginalAmount(liability) {
  // Calculate the total outstanding amount for credit accounts
  return calculateCreditTotalOutstandingAmount(liability.aprs);
}
function calculateStudentLoanOriginalAmount(liability) {
  return liability.origination_principal_amount;
}
*/
